import type { LanguageCode, TestDuration } from './wordLists';

export interface TestResult {
  wpm: number;
  cpm: number;
  accuracy: number;
  correctWords: number;
  wrongWords: number;
  keystrokes: number;
  errorRate: number;
  duration: TestDuration;
  language: LanguageCode;
  timestamp: string;
  wpmTimeline: number[];
}

export interface PersonalBest {
  wpm: number;
  accuracy: number;
  timestamp: string;
}

const STORAGE_KEY = 'typeswift_personal_bests';
const LEADERBOARD_KEY = 'typeswift_leaderboard';

export function getPersonalBest(
  language: LanguageCode,
  duration: TestDuration
): PersonalBest | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as Record<string, PersonalBest>;
    return data[`${language}-${duration}`] || null;
  } catch {
    return null;
  }
}

export function savePersonalBest(
  language: LanguageCode,
  duration: TestDuration,
  result: TestResult
): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const data: Record<string, PersonalBest> = raw ? JSON.parse(raw) : {};
    const key = `${language}-${duration}`;
    const existing = data[key];
    if (!existing || result.wpm > existing.wpm) {
      data[key] = {
        wpm: result.wpm,
        accuracy: result.accuracy,
        timestamp: result.timestamp,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export interface LeaderboardEntry {
  id: string;
  wpm: number;
  accuracy: number;
  language: LanguageCode;
  duration: TestDuration;
  timestamp: string;
}

export function getLeaderboard(): LeaderboardEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LEADERBOARD_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw) as LeaderboardEntry[];
    return data.sort((a, b) => b.wpm - a.wpm).slice(0, 10);
  } catch {
    return [];
  }
}

export function addLeaderboardEntry(entry: Omit<LeaderboardEntry, 'id'>): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(LEADERBOARD_KEY);
    const data: LeaderboardEntry[] = raw ? JSON.parse(raw) : [];
    data.push({ ...entry, id: `entry-${entry.timestamp}-${entry.wpm}` });
    const sorted = data.sort((a, b) => b.wpm - a.wpm).slice(0, 20);
    localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(sorted));
  } catch {
    // silent fail
  }
}

export interface LanguageBest {
  language: LanguageCode;
  wpm: number;
  accuracy: number;
  duration: TestDuration;
  timestamp: string;
  attempts: number;
}

/** The user's best run per language, derived from their saved personal bests. */
export function getBestPerLanguage(): LanguageBest[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const data = JSON.parse(raw) as Record<string, PersonalBest>;
    const byLang: Record<string, LanguageBest> = {};
    for (const [key, pb] of Object.entries(data)) {
      const sep = key.lastIndexOf('-');
      const language = key.slice(0, sep) as LanguageCode;
      const duration = parseInt(key.slice(sep + 1)) as TestDuration;
      const existing = byLang[language];
      if (existing) existing.attempts += 1;
      if (!existing || pb.wpm > existing.wpm) {
        byLang[language] = {
          language,
          wpm: pb.wpm,
          accuracy: pb.accuracy,
          duration,
          timestamp: pb.timestamp,
          attempts: existing ? existing.attempts : 1,
        };
      }
    }
    return Object.values(byLang).sort((a, b) => b.wpm - a.wpm);
  } catch {
    return [];
  }
}
